/**
 * Store de política de sesión — timeout por inactividad (GAMP 5 / 21 CFR Part 11 §11.10(d)).
 *
 * Modelo:
 *   - timeoutMinutos: minutos de inactividad antes de auto-logout (default 30).
 *   - warningMinutos: minutos antes del timeout en que se muestra warning toast.
 *   - lastActivityAt: ISO timestamp del último input del usuario (mousemove, click,
 *     keydown). Se actualiza por throttle desde useSessionTimeout.
 *
 * El cumplimiento se hace en el hook `useSessionTimeout`, no en el store. El store
 * solo expone configuración y estado de actividad. Persistido a localStorage para
 * que multi-tab pueda sincronizar.
 *
 * Regulatorio: ANMAT exige timeout automático para usuarios que abandonen la
 * estación de trabajo (defensa contra registro fraudulento por un tercero).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const DEFAULT_TIMEOUT = 30; // minutos
const DEFAULT_WARNING = 2; // minutos

/** Mínimos defendibles. Bajar del mínimo es contraproducente (false positives). */
export const TIMEOUT_MIN_MINUTOS = 5;
export const TIMEOUT_MAX_MINUTOS = 120;
export const WARNING_MIN_MINUTOS = 1;

interface PoliticaSesionState {
  timeoutMinutos: number;
  warningMinutos: number;
  lastActivityAt: string | null;

  setTimeout: (minutos: number) => void;
  setWarning: (minutos: number) => void;
  touchActivity: () => void;
  reset: () => void;
}

export const usePoliticaSesionStore = create<PoliticaSesionState>()(
  persist(
    (set) => ({
      timeoutMinutos: DEFAULT_TIMEOUT,
      warningMinutos: DEFAULT_WARNING,
      lastActivityAt: null,

      setTimeout: (minutos) => {
        const clamped = Math.max(TIMEOUT_MIN_MINUTOS, Math.min(TIMEOUT_MAX_MINUTOS, minutos));
        set({ timeoutMinutos: clamped });
      },

      setWarning: (minutos) => {
        const clamped = Math.max(WARNING_MIN_MINUTOS, minutos);
        set({ warningMinutos: clamped });
      },

      touchActivity: () => {
        set({ lastActivityAt: new Date().toISOString() });
      },

      reset: () => set({
        timeoutMinutos: DEFAULT_TIMEOUT,
        warningMinutos: DEFAULT_WARNING,
        lastActivityAt: null,
      }),
    }),
    {
      name: 'riga-traza-politica-sesion',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/**
 * Calcula cuántos ms quedan hasta el auto-logout dado un lastActivityAt + timeout.
 * Devuelve número negativo si ya expiró.
 */
export function msHastaTimeout(
  lastActivityAt: string | null,
  timeoutMinutos: number,
  now: number = Date.now(),
): number {
  if (!lastActivityAt) return timeoutMinutos * 60_000;
  const last = new Date(lastActivityAt).getTime();
  return last + timeoutMinutos * 60_000 - now;
}
